import { ControllerHand, getQueryParameter, InputDeviceType, MulticastDelegate1 } from "flavr-common";
import { Vector3, Quaternion } from "flavr-math";
import { renderManager } from "../../xr/RenderManager";
import { Group } from "three";
import { ControllerHandObject } from "./ControllerInformation";
import { preferencesService } from "../../preferences/PreferencesService";
import { xrService } from "@modules/xr/XRService";
const tmpQuaternion = new Quaternion();
/**
 * Corrections applied after taking the WebXR wrist joint orientation so that the
 * static hand mesh (Hand_A_17) – which was designed for the grip-space coordinate
 * frame – is displayed correctly on remote peers.
 *
 * On Meta Quest the wrist joint's +X axis points towards the dorsal surface (back
 * of the hand) rather than to the radial/index side, leaving the model twisted
 * ~90° around the forward (ray) axis compared to grip space.
 *
 * The left and right wrist joints have mirrored X-axes, so the required Z-rotation
 * has opposite signs for each hand:
 *   Left  hand: −90° around Z  (−π/2)
 *   Right hand: +90° around Z  (+π/2)
 */
const WRIST_TO_GRIP_CORRECTION_LEFT = new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), -Math.PI / 2);
const WRIST_TO_GRIP_CORRECTION_RIGHT = new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), +Math.PI / 2);
class GamepadDetectionService {
    constructor() {
        this.gamepadConnected$ = new MulticastDelegate1();
        this.gamepadDisconnected$ = new MulticastDelegate1();
        /** Fires whenever hasTrackedHand changes on an already-connected controller. */
        this.hasTrackedHandChanged$ = new MulticastDelegate1();
        // holds the current state of the gamepads gets automatically updated
        this.gamepads = new Map();
        this.sourceByHand = new Map();
        this.pendingDisconnectTimeouts = new Map();
        this.pendingDisconnectInfos = new Map();
        this.useMetaStreaming = true;
        this.lastPoseSecurityErrorLogTime = 0;
        const spaceStr = getQueryParameter(window.location.href, "inputSpace");
        if (spaceStr) {
            this.customInputSpace = spaceStr;
        }
    }
    clearGamepads() {
        for (const timeoutId of this.pendingDisconnectTimeouts.values()) {
            window.clearTimeout(timeoutId);
        }
        this.pendingDisconnectTimeouts.clear();
        this.pendingDisconnectInfos.clear();
        this.sourceByHand.clear();
        for (const gamepad of this.gamepads.values()) {
            this.gamepadDisconnected(gamepad);
        }
        this.gamepads.clear();
    }
    hasHandData(inputSource) {
        return inputSource.hand !== undefined && inputSource.hand !== null;
    }
    shouldUseSyntheticHandGamepad(inputSource) {
        if (!this.hasHandData(inputSource))
            return false;
        const profiles = Array.isArray(inputSource.profiles) ? inputSource.profiles : [];
        const hasControllerProfile = profiles.some((profile) => {
            return profile.includes("touch") || profile.includes("controller") || profile.includes("knuckles") || profile.includes("vive");
        });
        const hasHandProfile = profiles.some((profile) => {
            return profile.includes("hand");
        });
        const hasNativeGamepad = inputSource.gamepad !== undefined && inputSource.gamepad !== null;
        // Keep native controller buttons (e.g. Virtual Desktop / Touch controller profiles).
        if (hasControllerProfile && hasNativeGamepad) {
            return false;
        }
        // For real hand-tracking profiles, or when no native gamepad is exposed,
        // synthesize pinch/grab from joint distances.
        return hasHandProfile || !hasNativeGamepad;
    }
    isPoseUnavailableSecurityError(error) {
        if (!(error instanceof DOMException))
            return false;
        if (error.name !== "SecurityError")
            return false;
        return error.message.includes("Poses cannot be given out for the current state");
    }
    safeGetJointPose(xrFrame, jointSpace, referenceSpace) {
        if (xrFrame.getJointPose === undefined)
            return undefined;
        try {
            const pose = xrFrame.getJointPose(jointSpace, referenceSpace);
            return pose !== null && pose !== void 0 ? pose : undefined;
        }
        catch (error) {
            if (!this.isPoseUnavailableSecurityError(error)) {
                throw error;
            }
            const t = performance.now();
            if (t - this.lastPoseSecurityErrorLogTime > 2000) {
                this.lastPoseSecurityErrorLogTime = t;
                console.warn("[xr] getJointPose temporarily unavailable; skipping hand-pose update for this frame");
            }
            return undefined;
        }
    }
    usesTrackedHandRenderable(inputSource, _hand) {
        return this.hasHandData(inputSource);
    }
    updateInputSources(event) {
        var _a;
        for (let i = 0; i < event.removed.length; ++i) {
            const inputSource = event.removed[i];
            const gampadInfo = this.gamepads.get(inputSource);
            if (gampadInfo === undefined)
                continue;
            const hand = gampadInfo.hand;
            const activeSource = this.sourceByHand.get(hand);
            // Ignore stale remove events for non-active sources of the same hand.
            if (activeSource !== undefined && activeSource !== inputSource) {
                this.gamepads.delete(inputSource);
                continue;
            }
            this.gamepads.delete(inputSource);
            this.sourceByHand.delete(hand);
            // If another input source for the same hand is still active, keep controller alive.
            let hasAlternativeSourceForSameHand = false;
            for (const [, info] of this.gamepads) {
                if (info.hand === gampadInfo.hand) {
                    hasAlternativeSourceForSameHand = true;
                    break;
                }
            }
            if (hasAlternativeSourceForSameHand) {
                continue;
            }
            // Some runtimes briefly flip between hand/controller input sources.
            // Delay disconnect to avoid tearing down and recreating the same controller.
            this.pendingDisconnectInfos.set(hand, gampadInfo);
            const prevTimeout = this.pendingDisconnectTimeouts.get(hand);
            if (prevTimeout !== undefined) {
                window.clearTimeout(prevTimeout);
            }
            const timeoutId = window.setTimeout(() => {
                this.pendingDisconnectTimeouts.delete(hand);
                const pendingInfo = this.pendingDisconnectInfos.get(hand);
                if (pendingInfo === gampadInfo) {
                    this.pendingDisconnectInfos.delete(hand);
                    this.gamepadDisconnected(gampadInfo);
                }
            }, 400);
            this.pendingDisconnectTimeouts.set(hand, timeoutId);
        }
        for (let i = 0; i < event.added.length; ++i) {
            const inputSource = event.added[i];
            const hand = this.getInputSourceHandedness(inputSource);
            const gamepad = this.shouldUseSyntheticHandGamepad(inputSource)
                ? this.createSyntheticHandGamepad(inputSource)
                : ((_a = inputSource.gamepad) !== null && _a !== void 0 ? _a : this.createSyntheticHandGamepad(inputSource));
            if (gamepad === undefined)
                continue;
            const type = this.getGamepadType(inputSource);
            if (type === undefined) {
                console.warn("Gamepad type from inputSource is not supported", inputSource.profiles);
                continue;
            }
            // If there is already a source for this hand, merge into the existing controller info.
            let existingSourceForHand = this.sourceByHand.get(hand);
            let existingInfoForHand = existingSourceForHand !== undefined ? this.gamepads.get(existingSourceForHand) : undefined;
            if (existingInfoForHand === undefined) {
                for (const [source, info] of this.gamepads) {
                    if (info.hand === hand) {
                        existingSourceForHand = source;
                        existingInfoForHand = info;
                        break;
                    }
                }
            }
            if (existingInfoForHand !== undefined && existingSourceForHand !== undefined) {
                if (existingSourceForHand !== inputSource) {
                    this.gamepads.delete(existingSourceForHand);
                    existingInfoForHand.gamepad = gamepad;
                    existingInfoForHand.type = type;
                    const newHasTrackedHand = this.usesTrackedHandRenderable(inputSource, hand);
                    const trackedHandChanged = existingInfoForHand.hasTrackedHand !== newHasTrackedHand;
                    existingInfoForHand.hasTrackedHand = newHasTrackedHand;
                    existingInfoForHand.handObject.showHand = this.hasHandData(inputSource);
                    this.gamepads.set(inputSource, existingInfoForHand);
                    this.sourceByHand.set(hand, inputSource);
                    if (existingInfoForHand.connected && trackedHandChanged) {
                        this.hasTrackedHandChanged$.invoke(existingInfoForHand);
                    }
                }
                continue;
            }
            const pendingInfo = this.pendingDisconnectInfos.get(hand);
            if (pendingInfo !== undefined) {
                const timeoutId = this.pendingDisconnectTimeouts.get(hand);
                if (timeoutId !== undefined) {
                    window.clearTimeout(timeoutId);
                    this.pendingDisconnectTimeouts.delete(hand);
                }
                this.pendingDisconnectInfos.delete(hand);
                pendingInfo.gamepad = gamepad;
                pendingInfo.type = type;
                const newHasTrackedHand = this.usesTrackedHandRenderable(inputSource, hand);
                const trackedHandChanged = pendingInfo.hasTrackedHand !== newHasTrackedHand;
                pendingInfo.hasTrackedHand = newHasTrackedHand;
                pendingInfo.handObject.showHand = this.hasHandData(inputSource);
                this.gamepads.set(inputSource, pendingInfo);
                this.sourceByHand.set(hand, inputSource);
                // pendingInfo.connected is still true – notify listeners of the tracking-mode change.
                if (pendingInfo.connected && trackedHandChanged) {
                    this.hasTrackedHandChanged$.invoke(pendingInfo);
                }
                continue;
            }
            const newGamepadInfo = {
                gamepad,
                type,
                hand,
                handObject: new ControllerHandObject(),
                hasTrackedHand: this.usesTrackedHandRenderable(inputSource, hand),
                position: new Vector3(),
                orientation: new Quaternion(),
                visualPosition: undefined,
                visualOrientation: undefined,
                rayOrigin: new Vector3(),
                rayRotation: new Quaternion(),
                hasLinearVelocity: false,
                linearVelocity: new Vector3(),
                hasAngularVelocity: false,
                angularVelocity: new Vector3(),
                connected: false,
                joystickEnabled: true
            };
            newGamepadInfo.handObject.showHand = this.hasHandData(inputSource);
            this.gamepads.set(inputSource, newGamepadInfo);
            this.sourceByHand.set(hand, inputSource);
        }
    }
    createSyntheticHandGamepad(inputSource) {
        if (inputSource.hand === undefined || inputSource.hand === null)
            return undefined;
        // Two mutable button objects – values are updated from joint data each frame.
        const syntheticSelectButton = { value: 0, pressed: false, touched: false };
        const syntheticGrabButton = { value: 0, pressed: false, touched: false };
        const syntheticGamepad = {
            id: `synthetic-hand-${inputSource.handedness || "unknown"}`,
            index: -1,
            connected: true,
            mapping: "xr-standard",
            timestamp: performance.now(),
            axes: [],
            buttons: [syntheticSelectButton, syntheticGrabButton],
            hapticActuators: []
        };
        return syntheticGamepad;
    }
    update(xrFrame) {
        if (xrFrame === undefined || !xrService.isPresenting())
            return;
        for (const [inputSource, gampadInfo] of this.gamepads) {
            this.updateGamepadPose(xrFrame, inputSource, gampadInfo);
        }
    }
    getControllerInformationByHandedness(hand) {
        return Array.from(this.gamepads.values()).filter((info) => info.hand === hand);
    }
    getControllerInformationForInputSource(inputSource) {
        const bySource = this.gamepads.get(inputSource);
        if (bySource !== undefined)
            return bySource;
        const hand = this.getInputSourceHandedness(inputSource);
        const activeSource = this.sourceByHand.get(hand);
        if (activeSource !== undefined) {
            return this.gamepads.get(activeSource);
        }
        return undefined;
    }
    getInputSourceHandedness(inputSource /* XRInputSource */) {
        if (inputSource.handedness === "right") {
            return ControllerHand.RightHand;
        }
        if (inputSource.handedness === "left") {
            return ControllerHand.LeftHand;
        }
        return ControllerHand.Unknown;
    }
    gamepadDisconnected(gamepadInfo) {
        if (!gamepadInfo.connected)
            return; // already disconnected
        gamepadInfo.connected = false;
        this.gamepadDisconnected$.invoke(gamepadInfo);
    }
    gamepadConnected(gamepadInfo) {
        gamepadInfo.connected = true;
        this.gamepadConnected$.invoke(gamepadInfo);
    }
    getGamepadType(inputSource /* XRInputSource */) {
        const profiles = inputSource.profiles;
        for (const profile of profiles) {
            const type = GamepadDetectionService.inputSourceProfileNameToType[profile];
            if (type !== undefined)
                return type;
        }
        for (const profile of profiles) {
            if (profile.includes("vive") || profile.includes("steam") || profile.includes("index")) {
                return InputDeviceType.ValveIndexController;
            }
            if (profile.includes("oculus") || profile.includes("quest") || profile.includes("meta")) {
                return InputDeviceType.OculusController;
            }
        }
        // return undefined;
        return InputDeviceType.ValveIndexController;
    }
    updateGamepadPose(xrFrame, inputSource, gampadInfo) {
        // During transient XR states (e.g. blurred/hidden) browsers can deny joint poses.
        if (xrFrame.session.visibilityState !== "visible")
            return;
        const referenceSpace = renderManager.xrManager.getReferenceSpace();
        if (!referenceSpace)
            return;
        gampadInfo.hasTrackedHand = this.usesTrackedHandRenderable(inputSource, gampadInfo.hand);
        gampadInfo.visualPosition = undefined;
        gampadInfo.visualOrientation = undefined;
        if (inputSource.hand) {
            gampadInfo.handObject.hasHandPose = true;
            gampadInfo.handObject.group.visible = true;
            for (const inputjoint of inputSource.hand.values()) {
                // getJointPose returns XRJointPose | null (null = pose temporarily unavailable).
                // Treat both null and undefined as "no pose" to avoid accessing null.transform.
                const jointPose = this.safeGetJointPose(xrFrame, inputjoint, referenceSpace);
                let joint = gampadInfo.handObject.joints.get(inputjoint.jointName);
                if (joint === undefined) {
                    joint = new Group();
                    joint.matrixAutoUpdate = false;
                    joint.visible = false;
                    gampadInfo.handObject.joints.set(inputjoint.jointName, joint);
                }
                if (jointPose != null) {
                    joint.position.copy(jointPose.transform.position);
                    joint.quaternion.copy(jointPose.transform.orientation);
                    joint.jointRadius = jointPose.radius;
                }
                joint.visible = jointPose != null;
            }
        }
        else {
            gampadInfo.handObject.hasHandPose = false;
            gampadInfo.handObject.group.visible = false;
        }
        let space;
        if (inputSource.hand !== undefined && inputSource.hand !== null) {
            space = inputSource.targetRaySpace;
        }
        if (this.customInputSpace) {
            if (this.customInputSpace === "grip") {
                if (inputSource.gripSpace) {
                    space = inputSource.gripSpace;
                }
            }
            if (!space) {
                space = inputSource.targetRaySpace;
            }
        }
        else if (space === undefined) {
            space = inputSource.gripSpace !== undefined ? inputSource.gripSpace : inputSource.targetRaySpace;
        }
        const pose = xrFrame.getPose(space, referenceSpace);
        if (pose) {
            gampadInfo.position.copy(pose.transform.position);
            gampadInfo.orientation.copy(pose.transform.orientation);
            if (inputSource.hand) {
                if (gampadInfo.visualPosition === undefined) {
                    gampadInfo.visualPosition = new Vector3();
                }
                if (gampadInfo.visualOrientation === undefined) {
                    gampadInfo.visualOrientation = new Quaternion();
                }
                // For tracked hands, replace the targetRaySpace position/orientation with
                // the visual hand pose only. Selection keeps using targetRaySpace.
                // The targetRaySpace has a pointer-direction coordinate
                // frame (Y toward palm = downward when palm faces floor on Meta Quest) that
                // makes the static hand model appear upside-down to other users.
                // The wrist joint frame has +Y toward the back of the hand (upward when
                // palm faces floor), which matches the grip-space convention the hand
                // model was designed for.
                let wristPoseApplied = false;
                if (gampadInfo.handObject.showHand) {
                    // XR hand data is active – wrist data is already in the joints map.
                    const wristGroup = gampadInfo.handObject.joints.get("wrist");
                    if (wristGroup === null || wristGroup === void 0 ? void 0 : wristGroup.visible) {
                        gampadInfo.visualPosition.copy(wristGroup.position);
                        gampadInfo.visualOrientation.copy(wristGroup.quaternion);
                        wristPoseApplied = true;
                    }
                }
                if (!wristPoseApplied && xrFrame.getJointPose) {
                    // Fallback: fetch just the wrist if the cached joint entry is not usable yet.
                    for (const jointSpace of inputSource.hand.values()) {
                        if (jointSpace.jointName === "wrist") {
                            const wristPose = this.safeGetJointPose(xrFrame, jointSpace, referenceSpace);
                            if (wristPose) {
                                gampadInfo.visualPosition.copy(wristPose.transform.position);
                                gampadInfo.visualOrientation.copy(wristPose.transform.orientation);
                                wristPoseApplied = true;
                            }
                            break;
                        }
                    }
                }
                if (!wristPoseApplied) {
                    gampadInfo.visualPosition.copy(gampadInfo.position);
                    gampadInfo.visualOrientation.copy(gampadInfo.orientation);
                }
                // The WebXR wrist joint frame is rotated ~90° around the forward/ray axis
                // relative to the grip-space convention the static hand mesh was designed for.
                // Left and right wrists have mirrored X-axes → opposite Z-rotation signs.
                const wristCorrection = gampadInfo.hand === ControllerHand.RightHand
                    ? WRIST_TO_GRIP_CORRECTION_RIGHT
                    : WRIST_TO_GRIP_CORRECTION_LEFT;
                gampadInfo.visualOrientation.multiply(wristCorrection);
                this.calculateRayRelativeTransform(xrFrame, inputSource, gampadInfo, pose);
            }
            else if (this.useMetaStreaming) {
                // Position offset for Meta Quest Link controller streaming.
                // Only applied for physical controllers – never for tracked hands,
                // since the wrist joint pose above is already correctly positioned.
                const offset = new Vector3(0.014, 0.00, -0.11).applyQuaternion(pose.transform.orientation);
                gampadInfo.position.add(offset);
            }
            if (pose.linearVelocity) {
                gampadInfo.hasLinearVelocity = true;
                gampadInfo.linearVelocity.copy(pose.linearVelocity);
            }
            else {
                gampadInfo.hasLinearVelocity = false;
            }
            if (pose.angularVelocity) {
                gampadInfo.hasAngularVelocity = true;
                gampadInfo.angularVelocity.copy(pose.angularVelocity);
            }
            else {
                gampadInfo.hasAngularVelocity = false;
            }
            if (!gampadInfo.connected) {
                this.initGamepadConnection(xrFrame, inputSource, gampadInfo, pose);
            }
        }
        else {
            console.warn(`Could not get pose from inputSource:${String(gampadInfo.uid)} with xrFrame.getPose`);
        }
    }
    initGamepadConnection(xrFrame, inputSource, gampadInfo, pose) {
        this.calculateRayRelativeTransform(xrFrame, inputSource, gampadInfo, pose);
        this.gamepadConnected(gampadInfo);
        this.useMetaStreaming = preferencesService.getPreferredValue("streaming", "meta") === "meta";
    }
    calculateRayRelativeTransform(xrFrame /* XRFrame */, inputSource, gampadInfo, pose /* XRPose */) {
        if (inputSource.hand !== undefined && inputSource.hand !== null) {
            gampadInfo.rayOrigin.set(0, 0, 0);
            gampadInfo.rayRotation.identity();
            return;
        }
        const calcPosition = true;
        if (inputSource.gripSpace === undefined)
            return;
        const referenceSpace = renderManager.xrManager.getReferenceSpace();
        let fallback = renderManager.isEmulatedXRDevice;
        if (!fallback) {
            const rayPose = xrFrame.getPose(inputSource.targetRaySpace, inputSource.gripSpace);
            if (rayPose !== undefined) {
                if (calcPosition) {
                    gampadInfo.rayOrigin.copy(rayPose.transform.position);
                }
                if (inputSource.hand) {
                    gampadInfo.rayRotation.identity();
                }
                else {
                    gampadInfo.rayRotation.copy(rayPose.transform.orientation);
                }
            }
            else {
                fallback = true;
            }
        }
        if (fallback && pose !== undefined && referenceSpace) {
            const rayPose = xrFrame.getPose(inputSource.targetRaySpace, referenceSpace);
            if (rayPose !== undefined) {
                gampadInfo.rayRotation.copy(pose.transform.inverse.orientation);
                gampadInfo.rayRotation.multiply(tmpQuaternion.copy(rayPose.transform.orientation));
                if (calcPosition) {
                    gampadInfo.rayOrigin.subVectors(rayPose.transform.position, pose.transform.position).applyQuaternion(gampadInfo.rayRotation);
                }
            }
        }
    }
}
GamepadDetectionService.inputSourceProfileNameToType = {
    "htc-vive-controller-mv": InputDeviceType.HtcViveController,
    "htc-vive-controller-pro-mv": InputDeviceType.HtcViveController,
    "htc-vive": InputDeviceType.HtcViveController,
    "valve-knuckles-left": InputDeviceType.ValveIndexController,
    "valve-knuckles-right": InputDeviceType.ValveIndexController,
    "valve-index": InputDeviceType.ValveIndexController,
    "oculus-touch": InputDeviceType.OculusController,
    "oculus-go": InputDeviceType.OculusController,
    // Quest hand-tracking profile strings. Hand-tracking in this app is Quest-only (see
    // HandTrackingControllerService.ts), so these must resolve to Oculus, not Valve Index - a wrong value here
    // gets baked into the controller's mesh permanently the first time it's picked (see createControllerModel()),
    // and never gets corrected later since switching back to a physical controller only toggles hasTrackedHand
    // rather than re-selecting the mesh.
    "oculus-hand": InputDeviceType.OculusController,
    "generic-hand": InputDeviceType.OculusController,
    "generic-hand-select": InputDeviceType.OculusController,
    "generic-trigger": InputDeviceType.ValveIndexController
};
export const gamepadDetectionService = new GamepadDetectionService();
